import{TranslationStateEnum}from'@graphql/__generated__/globalTypes';/**
 * The rules a language badge follows, kept as pure functions so every surface that shows badges
 * answers these questions the same way - and so they can be tested without a DOM.
 *//**
 * WHY a translation run for this language cannot be started - and, as `null`, that it can.
 *
 * Two conditions, and neither is "the badge was clicked":
 *
 * - the practice must have **activated** the language. `translateMissing` refuses a locale
 *   outside `office.all_enabled_locales`, so a run on a badge that is only visible because it
 *   already has content could produce nothing but an error toast. This also covers a practice
 *   without the AI feature: `OfficeType#available_locales` then reports the default locale
 *   alone, so no target locale is activated and nothing here can be started.
 * - the state must not be `COMPLETE`. There is nothing left to translate, so a run would spend
 *   AI budget to change nothing.
 *
 * A reason rather than a boolean, so the info modal's greyed-out button can say what is in the
 * way instead of just refusing.
 *
 * `canStartTranslationRun` is derived from this rather than the two being written side by side:
 * a disabled button whose tooltip disagrees with it is worse than no tooltip at all.
 *
 * `not-activated` wins when both apply. It is the actionable one - the practice can switch the
 * language on - while "nothing is missing" is a dead end, and a complete translation in a
 * language that is switched off is exactly the case where both are true.
 */export const runUnavailableReason=(status,activatedLocales)=>{if(!activatedLocales.includes(status.locale))return'not-activated';if(status.state===TranslationStateEnum.COMPLETE)return'complete';return null;};/**
 * Whether a translation run can be started - `runUnavailableReason` above with the reason thrown
 * away. Derived from it rather than written beside it: a disabled button whose tooltip disagrees
 * with it is worse than no tooltip at all.
 *
 * This used to be `isBadgeClickable`, back when it decided whether a badge could be clicked at
 * all. It no longer does - every badge is clickable, because a tablet has no hover and the
 * tooltip's information was unreachable there. The reasoning above survived the change: it now
 * decides whether the info modal's "generate" button is enabled, and whether a `NONE` badge may
 * skip that modal and confirm a run directly.
 */export const canStartTranslationRun=(status,activatedLocales)=>runUnavailableReason(status,activatedLocales)===null;/**
 * What clicking a badge does.
 *
 * `NONE` goes straight to the run confirmation: there is nothing to inform about in a language
 * the block does not exist in. Everything else opens the info modal, which repeats the tooltip's
 * three lines and offers the run from there - so the counts are reachable without a hover.
 *
 * The `canStartTranslationRun` guard on the `NONE` branch is defence in depth rather than a live
 * case: `LanguageBadgeList` only shows a `NONE` badge for an activated locale. Without it, a
 * practice without the AI feature could be sent to a confirmation for a run the server refuses.
 */export const badgeClickAction=(status,activatedLocales)=>status.state===TranslationStateEnum.NONE&&canStartTranslationRun(status,activatedLocales)?'run':'info';/**
 * The statuses that actually get a badge. The single answer to "is this language shown here?",
 * used by LanguageBadgeList to render and by a surface with a heading to decide whether to draw
 * that heading at all.
 *
 * It exists as one function because two of them disagreed. The section on the block show page
 * asked "does this object have anything to translate?" while the list asked "will any badge
 * show?", and on a practice without the AI feature the first is true and the second is false -
 * so the heading and its divider were drawn above nothing.
 *
 * Two conditions:
 *
 * - `totalCount > 0`: a locale with nothing to translate gets no badge. That is what hides
 *   badges on an empty consultation, which comes back with one `NONE` status per target locale
 *   and `totalCount: 0` on each - they appear as soon as the first block is added.
 * - the practice **activated** the locale. It then shows whatever its state, so an activated
 *   language that is untranslated shows red rather than staying invisible.
 *
 * A locale the practice has NOT activated gets no badge, even when a translation for it exists
 * (Marcel: "wenn die Praxis eine Sprache nicht aktiviert hat, dann soll die Praxis auch kein Badge
 * für die Sprache sehen"). That reverses the earlier rule, which showed anything that had content
 * on the grounds that an existing translation must never be hidden.
 *
 * It is not a cosmetic choice, and this is the half that makes it coherent: the server applies the
 * same rule to READING, in `Office#locale_readable?`. A non-activated language serves German, so a
 * badge for it would offer a translation that the very next click cannot show.
 */export const visibleStatuses=(statuses,activatedLocales)=>statuses.filter(status=>status.totalCount>0&&activatedLocales.includes(status.locale));/**
 * Whether a surface will draw any badge at all - so a heading, a divider or a whole section can
 * be left out rather than framing an empty row. Derived from `visibleStatuses`, never from a
 * second rule of its own.
 */export const hasVisibleBadges=(statuses,activatedLocales)=>visibleStatuses(statuses,activatedLocales).length>0;